const CORE_GUARDRAILS = [
    "## Core Guardrails",
    "",
    "Use these rules unless the user explicitly instructs otherwise:",
    "- Read before write when placement is ambiguous.",
    "- Preserve existing model structure; do not overwrite labels/headers unless explicitly requested.",
    "- Never write to A1 unless explicitly requested (or the target range explicitly includes A1).",
    "- When creating new content from scratch, avoid starting in Column A / Row 1 unless the workbook already uses that layout.",
    "- Avoid circular references.",
    "- Prefer formulas over hardcoded numbers; put assumptions in assumption/input cells (do not embed assumptions inside formulas).",
    "- Percentages as decimals in values/formulas (15% -> 0.15).",
    "- Be explicit about ranges: edits should always specify an explicit rangeRef or numeric range.",
    "- Add comments for hardcoded values and for any values sourced from external docs (cite file/page/range when possible).",
    "- Cross-sheet formulas: prefer quoting sheet names: `'Sheet Name'!A1` (always safe, avoids space/special-char bugs).",
    "- Dates: prefer explicit date strings like `MM/DD/YYYY` (or a `=DATE(YYYY,MM,DD)` formula) rather than Excel serial numbers.",
    "",
    "Tool schema reminders:",
    "- Reading uses A1 `targetRange` on read* tools.",
    "- Formatting uses editFormat with `targetRange` (A1) and pixel widths/heights.",
    "- Values/formulas/text use editContent (and editContent numberFormats for number formatting).",
].join("\n");
const EDIT_CONTENT_PLANNING = [
    "## EditContent: Planning + Placement",
    "",
    "Before writing:",
    "- Identify the exact sheet + range to touch (by reading structure first when needed).",
    "- If you need more space (new rows/cols), use editStructure; then account for index shifts.",
    "- For multi-step builds, prefer creating new sheets/sections rather than squeezing content into existing tables.",
    "- Leave breathing room between sections (blank rows/cols) so the workbook stays readable.",
    "",
    "Placement discipline:",
    "- Treat user intent like INSERT by default: avoid clobbering an existing model just because a range is convenient.",
    "- Never overwrite header labels (typically row 1) unless explicitly requested.",
    "- If you must overwrite, say so in your result rationale and keep the overwrite tightly scoped.",
].join("\n");
const EDIT_CONTENT_FORMULAS_AND_FILL = [
    "## EditContent: Formulas + Fill Discipline",
    "",
    "When filling formulas across a range (Excel fill-handle behavior):",
    "- Use relative references (A1) when the reference should move with the fill.",
    "- Use absolute references ($A$1 / $A1 / A$1) when anchors must stay fixed.",
    "- Cross-sheet references should use quoted sheet names: `'Sheet Name'!A1`.",
    "",
    "Examples:",
    "- Fill down with moving row: content '=A2*1.1' written to B2:B10 expands to A3, A4, ...",
    "- Use $A$1 when a single assumption cell should be used for all rows.",
    "",
    "Hardcoding guidance:",
    "- Do not embed constants like 1.07 or 0.35 inside many formulas; use assumption/input cells instead.",
    "- Only hardcode true constants (unit conversions, mathematical constants).",
    "- If a hardcoded number is unavoidable, add a comment citing its source (or stating it's a placeholder).",
    "",
    "Header discipline (critical):",
    "- When building formulas from existing model data, explicitly identify the row and column headers you mean (read structure first if needed).",
    "- Do not guess header positions; avoid off-by-one mistakes when referencing time-period columns.",
].join("\n");
const EDIT_CONTENT_NUMBER_FORMATS = [
    "## EditContent: Number Formats",
    "",
    "Preservation rule (parity with legacy):",
    "- Default is to preserve existing number formats. Only send numberFormats when you explicitly need to change the display type or when creating new content.",
    "",
    "Use editContent.numberFormats for number formatting (not editFormat):",
    "- Currency: use `currency` preset (or an explicit currency format string).",
    "- Percentages: format as percentage but values must be decimals (0.053 for 5.3%).",
    "- Years: use `year` preset (integer display) for values like 2024. Do NOT use `yyyy` to format numeric year values (that is a date format).",
    "- Dates: prefer explicit date strings like `MM/DD/YYYY` or date formulas; match existing workbook patterns.",
    "",
    "Supported preset names (recommended):",
    "- ignore, currency, number, percentage, multiple, year, FYyy, mm-yyyy, mm-yy, yyyy",
    "- You may also pass a custom Excel number format string (e.g. '#,##0.00').",
    "",
    "Financial modeling discipline:",
    "- Avoid adding currency symbols to every row; follow the workbook's pattern (often bookends / totals).",
].join("\n");
const EDIT_FORMAT_READ_BEFORE_FORMAT = [
    "## EditFormat: Read Before You Format",
    "",
    "Formatting should match the workbook's existing style:",
    "- ReadFormatting on a representative nearby range first (header + body + totals).",
    "- Apply formatting consistently across the full requested band, including in-band empty cells.",
    "- Prefer contiguous ranges and fewer calls.",
    "",
    "Color palette guidance:",
    "- Default to backend palette unless user requests otherwise.",
    "- Avoid introducing new accent colors just because they look nice.",
].join("\n");
const EDIT_FORMAT_BORDERS_AND_DIMENSIONS = [
    "## EditFormat: Borders + Dimensions",
    "",
    "Dimensions:",
    "- Column widths and row heights are in pixels in editFormat (not Excel char units, not points).",
    "- Pick practical defaults when not specified (columns ~80-220px, body rows ~20-28px) and match neighbors.",
    "",
    "Borders:",
    "- Prefer sparse, readable borders for financial tables (avoid boxing every cell unless requested).",
    "- Use top borders to separate sections and totals, and bottom borders for headers.",
    "- Keep border colors neutral (black/gray) unless style requires otherwise.",
].join("\n");
const EDIT_CHART_XML_WORKFLOWS = [
    "## EditChart: XML-first workflow",
    "",
    "- Prefer readExcel with readCharts before editing a chart so you have authoritative chartXml + readId (+ version hash).",
    "- Edit operations should be search/replace changes over chartXml when possible.",
    "- There is no standalone move action; position/size changes are XML edits.",
].join("\n");
const EDIT_CHART_VISUAL_BEST_PRACTICES = [
    "## Charts: Visual Best Practices (Legacy Parity)",
    "",
    "- Avoid 3D effects, heavy shadows, and chart junk.",
    "- Reserve bold colors for highlights/insights; keep most series muted and consistent.",
    "- For variance: green for positive, red for negative (unless the workbook uses a different convention).",
    "- Columns/bars typically start the y-axis at zero; use light gray horizontal gridlines only when needed.",
    "- Prefer readable labels over dense data labels; label only key points when crowded.",
    "- Keep consistent sizing/formatting across related charts.",
].join("\n");
const EDIT_CHART_CHARTXML_CONTRACT = [
    "## EditChart: chartXml Contract Reference",
    "",
    "chartXml is the authoritative edit surface for charts (similar in spirit to SlideML).",
    "",
    "Structure (simplified):",
    "- <excelChart version=\"2.0\" chartId=... sheetName=... chartType=... dataMode=...>",
    "-   <defs/> (optional)",
    "-   <placement/> <dataSource/> <title/> <legend/>",
    "-   <series/>... <categoryAxis/> <primaryValueAxis/> <secondaryValueAxis/>",
    "-   <chartArea/> <plotArea/> <barColumnOptions/> <pieOptions/> <lineOptions/> <waterfallOptions/>",
    "-   <fyi/>",
    "- </excelChart>",
    "",
    "Important:",
    "- chartXml is real XML (v2). Do NOT use JSON-in-CDATA.",
    "- defs/ref are global; editing a def affects all references.",
    "- Keep search fragments unique; each change must match exactly once.",
    "",
    "Edit workflow:",
    "- Prefer calling readExcel with readCharts first to get chartXml + readId (+ chart image when available).",
    "- If you omit chartId/chartName, readCharts returns ALL charts on that sheet (for discovery).",
    "- For edit, send changes[] with exact search/replace substrings over chartXml.",
    "- Each search must match exactly once (0 matches = not found, >1 matches = ambiguous).",
    "- If the chart changed since your last read, re-read and retry against the latest chartXml.",
    "",
    "Placement is an XML edit (no standalone move action):",
    "- placement may include anchorCell and widthPixels/heightPixels.",
    "",
    "Axis titles:",
    "- To remove the default 'Axis Title' placeholder, set primaryValueAxis.title.show=false (don't set an empty string).",
].join("\n");
const CHART_DATA_RANGES_AND_TYPES = [
    "## Charts: Data Ranges and Chart Types (Legacy Parity)",
    "",
    "Critical data range rule:",
    "- Exclude headers from ALL numeric series ranges.",
    "- categoryRange should contain only the category labels (no header).",
    "- series[].valuesRange should contain only numeric values (no header).",
    "",
    "chartConfig.dataSource shape:",
    "- dataSource.categoryRange: A1 range (optionally sheet-qualified)",
    "- dataSource.series[]: { index, name|nameRange, valuesRange }",
    "",
    "Chart type compatibility groups (use compatible types together):",
    "- Column/Bar/Line group: column*, bar*, line* (can combine; secondary axis is allowed).",
    "- Scatter group: scatter* and bubble (X-Y coordinate system).",
    "- Standalone-only: pie/doughnut, waterfall, funnel, treemap, sunburst, histogram, boxWhisker, pareto.",
    "",
    "Canonical chartType strings (preferred):",
    "- columnClustered, columnStacked, columnStacked100",
    "- barClustered, barStacked, barStacked100",
    "- line, lineMarkers, lineStacked, lineStacked100",
    "- area, areaStacked, areaStacked100",
    "- pie, doughnut, waterfall, funnel, treemap, sunburst, histogram, boxWhisker, pareto",
].join("\n");
const CHART_FOOTBALL_FIELD_GUIDELINES = [
    "## Charts: Football Field (Stacked Bar) Guidelines",
    "",
    "Football field charts visualize a range (low-to-high) with a highlighted median/central value.",
    "",
    "Core construction:",
    "- Use a stacked bar chart: chartType = barStacked (columnStacked is rare).",
    "- Include all helper series in dataSource (do NOT delete them).",
    "- Make helper series visually transparent; only the median/difference series should be visible.",
    "",
    "Transparency in chartConfig.series[] (Office.js-friendly):",
    "- For helper series: use fill.clear=true and line.visible=false.",
    "- fill.transparency is not directly writable in Office.js chart APIs.",
    "- Keep data labels ON for helper series so the low/high values are readable.",
    "",
    "Data label positioning (canonical positions):",
    "- Low series label: position=insideEnd",
    "- High series label: position=insideBase",
    "- Visible median/difference series: often no labels (avoid clutter).",
    "",
    "Legend:",
    "- Football field charts typically have legend.show=false.",
    "",
    "Axis bounds (critical):",
    "- Set primaryValueAxis.bounds.minimum and .maximum explicitly (must not be null).",
    "- Minimum should be slightly below the LOW series min; maximum slightly above the HIGH series max.",
    "- Use tight but not cramped margins (e.g. 3-10% padding depending on scale).",
].join("\n");
const CHART_WATERFALL_GUIDELINES = [
    "## Charts: Waterfall Guidelines + Totals",
    "",
    "Waterfall charts are used for bridges (start value -> deltas -> end value).",
    "",
    "Chart type:",
    "- chartType = waterfall",
    "- Note: Some Office.js hosts don't implement native waterfall chartType. If it fails, use a stacked-column workaround (chartType=columnStacked with helper series).",
    "",
    "Totals / subtotals (Office.js gap + COM fallback):",
    "- Excel's \"Set as Total\" is Point.IsTotal.",
    "- Office.js cannot set Point.IsTotal directly.",
    "- To request totals, include waterfallOptions.totalPoints in chartConfig (0-based indices).",
    "- The add-in will invoke a minimal COM backend fallback ONLY when totalPoints is present.",
    "",
    "Common pattern:",
    "- Set the first and last points as totals: totalPoints=[0, lastIndex].",
    "",
    "Optional styling knobs (when supported):",
    "- waterfallOptions.showConnectorLines (boolean)",
    "- waterfallOptions.increaseColor/decreaseColor/totalColor are best-effort color mappings, not true native waterfall category colors.",
    "",
    "Axis guidance:",
    "- If deltas are visually lost (tiny vs big totals), consider raising primaryValueAxis.bounds.minimum above 0 to zoom in.",
    "- Always keep units consistent with underlying data (percent series use decimals).",
].join("\n");
const CHART_DUAL_AXIS_GUIDELINES = [
    "## Charts: Dual Axis (Secondary Axis) Guidelines",
    "",
    "Use a secondary axis only when there are two meaningful scales that should be compared visually.",
    "",
    "How to request a secondary axis in chartConfig:",
    "- Set series[i].axisGroup = 'secondary' for the series that needs a secondary scale.",
    "- Provide secondaryValueAxis.bounds.minimum/maximum so the scale is readable.",
    "",
    "Scaling discipline:",
    "- State min/max of each series in raw numeric units from the sheet.",
    "- Percent axes use decimals (0.15 for 15%). Do not mix % units with 0-100 values.",
    "- Do not introduce a third scale; if 3+ scales exist, split into separate charts.",
].join("\n");
const FINANCIAL_MODELING_CONVENTIONS = [
    "## Financial Modeling Conventions",
    "",
    "- Prefer an assumptions area (rates, growth, margins) rather than repeated hardcoded constants.",
    "- Keep formulas readable and consistent across a column (avoid one-off overrides).",
    "- When changing structure, preserve totals/subtotals and check dependency chains (formulaTrace if needed).",
].join("\n");
const FINANCIAL_MODEL_STRUCTURE_AND_SPACING = [
    "## Financial Models: Structure, Spacing, and Time Headers",
    "",
    "- Use a clear flow: Assumptions -> Calculations -> Output.",
    "- Prefer separate sheets for major statements/schedules (Income Statement, Balance Sheet, Cash Flow, Assumptions).",
    "- Keep historical periods on the left and projections on the right.",
    "- Align time headers across sections (FY19 values should be in the same column everywhere).",
    "- Prefer stacking sections vertically (not horizontally) to keep time headers aligned.",
    "- Leave breathing room: blank rows between major sections/subsections.",
    "- Do not place time period headers in the same row as line items; keep headers on a dedicated row above values.",
].join("\n");
const FINANCIAL_MODEL_PROJECTIONS_ASSUMPTIONS_AND_NOTES = [
    "## Financial Models: Projections, Assumptions, and Notes",
    "",
    "- Never embed assumptions inside formulas; put each assumption in a dedicated input cell and reference it.",
    "- For projection line items, consider an explanatory row under the line item (e.g. 'YoY Growth', 'Margin') and link it to a central assumptions area.",
    "- Prefer consistent row formulas across a time series (avoid one-off overrides unless explicitly requested).",
    "- Fill the entire requested time horizon; do not leave partial projections.",
    "- Add a notes/comment mechanism for key drivers and citations (what the assumption is, and where it came from).",
    "- If pulling from an existing model, link via formulas instead of re-typing hardcoded values (unless user requests hardcoding).",
].join("\n");
const FINANCIAL_MODEL_BALANCE_CHECKS_AND_PLUGS = [
    "## Financial Models: Balance Checks and Plugs",
    "",
    "- Preserve existing balance checks and error checks; do not break them silently.",
    "- For balance sheet changes, prefer a plug method (e.g. cash plug) when needed to maintain balancing (unless the workbook uses a different convention).",
    "- Avoid circularity; if circular logic is required by the workbook, use a clear switch/circuit breaker pattern rather than accidental circular references.",
    "- When results look wrong (unexpected blanks/errors), use formulaTrace on key cells to locate the root cause.",
].join("\n");
const BASE_SECTIONS = [
    {
        id: "excel.core.guardrails",
        title: "Core Guardrails",
        tags: ["core", "guardrails"],
        content: CORE_GUARDRAILS,
    },
    {
        id: "excel.editContent.planning_and_placement",
        title: "EditContent Planning and Placement",
        tags: ["editContent", "planning", "placement"],
        content: EDIT_CONTENT_PLANNING,
    },
    {
        id: "excel.editContent.formulas_and_fill",
        title: "EditContent Formulas and Fill Discipline",
        tags: ["editContent", "formulas", "fill", "references"],
        content: EDIT_CONTENT_FORMULAS_AND_FILL,
    },
    {
        id: "excel.editContent.number_formats",
        title: "EditContent Number Formats",
        tags: ["editContent", "numberFormats", "currency", "percentage"],
        content: EDIT_CONTENT_NUMBER_FORMATS,
    },
    {
        id: "excel.editContent.sheet_refs_and_dates",
        title: "EditContent Sheet References and Dates",
        tags: ["editContent", "formulas", "dates", "references"],
        content: [
            "## EditContent: Sheet References and Dates (Legacy Parity)",
            "",
            "- Cross-sheet references: always quote the sheet name: `'Sheet Name'!A1`.",
            "- Do not reference sheets that do not exist (create the sheet first, or read listSheets/readContent to confirm).",
            "- For external workbook references, Excel syntax is usually `'[Workbook.xlsx]Sheet'!A1` (only if that workbook is actually available).",
            "- Dates: prefer explicit `MM/DD/YYYY` strings for literals, or use date formulas like `=DATE(2021,9,5)`.",
            "- Avoid writing raw Excel date serials (e.g. 44444) unless the workbook explicitly uses that pattern.",
        ].join("\n"),
    },
    {
        id: "excel.editFormat.read_before_format",
        title: "EditFormat Read Before You Format",
        tags: ["editFormat", "formatting", "style"],
        content: EDIT_FORMAT_READ_BEFORE_FORMAT,
    },
    {
        id: "excel.editFormat.borders_and_dimensions",
        title: "EditFormat Borders and Dimensions",
        tags: ["editFormat", "borders", "dimensions", "pixels"],
        content: EDIT_FORMAT_BORDERS_AND_DIMENSIONS,
    },
    {
        id: "excel.editChart.xml_workflows",
        title: "EditChart XML Workflows",
        tags: ["editChart", "charts", "xml"],
        content: EDIT_CHART_XML_WORKFLOWS,
    },
    {
        id: "excel.editChart.chartXml_contract",
        title: "EditChart chartXml Contract Reference",
        tags: ["editChart", "chartXml", "xml", "readId", "version", "changes"],
        content: EDIT_CHART_CHARTXML_CONTRACT,
    },
    {
        id: "excel.charts.data_ranges_and_types",
        title: "Charts Data Ranges and Types",
        tags: ["charts", "data", "ranges", "chartType"],
        content: CHART_DATA_RANGES_AND_TYPES,
    },
    {
        id: "excel.charts.football_field",
        title: "Football Field Chart Guidelines",
        tags: ["charts", "football", "football_field", "barStacked", "labels", "axis"],
        content: CHART_FOOTBALL_FIELD_GUIDELINES,
    },
    {
        id: "excel.charts.waterfall",
        title: "Waterfall Chart Guidelines and Totals",
        tags: ["charts", "waterfall", "bridge", "totals", "IsTotal", "com"],
        content: CHART_WATERFALL_GUIDELINES,
    },
    {
        id: "excel.charts.dual_axis",
        title: "Dual Axis (Secondary Axis) Guidelines",
        tags: ["charts", "secondary-axis", "dual-axis", "axisGroup"],
        content: CHART_DUAL_AXIS_GUIDELINES,
    },
    {
        id: "excel.charts.visual_best_practices",
        title: "Charts Visual Best Practices",
        tags: ["charts", "visual", "best-practices"],
        content: EDIT_CHART_VISUAL_BEST_PRACTICES,
    },
    {
        id: "excel.financial_modeling.conventions",
        title: "Financial Modeling Conventions",
        tags: ["financial", "modeling"],
        content: FINANCIAL_MODELING_CONVENTIONS,
    },
    {
        id: "excel.financial_modeling.structure_and_spacing",
        title: "Financial Model Structure and Spacing",
        tags: ["financial", "modeling", "structure", "headers"],
        content: FINANCIAL_MODEL_STRUCTURE_AND_SPACING,
    },
    {
        id: "excel.financial_modeling.projections_assumptions_notes",
        title: "Financial Model Projections and Assumptions",
        tags: ["financial", "modeling", "assumptions", "projections"],
        content: FINANCIAL_MODEL_PROJECTIONS_ASSUMPTIONS_AND_NOTES,
    },
    {
        id: "excel.financial_modeling.balance_checks_and_plugs",
        title: "Financial Model Balance Checks and Plugs",
        tags: ["financial", "modeling", "balance", "circularity"],
        content: FINANCIAL_MODEL_BALANCE_CHECKS_AND_PLUGS,
    },
];
function buildIndexContent(sections) {
    const lines = [
        "## Excel Prompt Library (Index)",
        "",
        "This guidance is stored as a queryable library so we do not inject everything into the system prompt.",
        "",
        "How to use:",
        "- Use promptSearch with a natural query (e.g. 'currency symbols', 'fill down absolute references', 'borders totals row').",
        "- Then use promptGet with the returned section IDs to retrieve the full text.",
        "",
        "Available sections:",
        ...sections.map((section) => {
            const tags = section.tags.length ? ` [tags: ${section.tags.join(", ")}]` : "";
            return `- ${section.id}: ${section.title}${tags}`;
        }),
    ];
    return lines.join("\n");
}
export const EXCEL_PROMPT_SECTIONS = [
    {
        id: "excel.promptEnv.index",
        title: "Prompt Library Index",
        tags: ["index"],
        content: buildIndexContent(BASE_SECTIONS),
    },
    ...BASE_SECTIONS,
];
const SECTION_BY_ID = new Map(EXCEL_PROMPT_SECTIONS.map((section) => [section.id, section]));
export function listExcelPromptSections() {
    return EXCEL_PROMPT_SECTIONS.map(({ id, title, tags }) => ({ id, title, tags }));
}
export function getExcelPromptSection(id) {
    return SECTION_BY_ID.get(id);
}
